<script setup>
import HrDragHandle from './HrDragHandle.vue';
import HrListItem from './HrListItem.vue';

/**
 * A row for HrSortableList — a tappable list item with a built-in drag handle.
 *
 * Bakes in the sortable chrome so rows are consistent and the handle contract HrSortableList's
 * pickup relies on is always satisfied: the drag handle (immediate grip) sits in the leading slot.
 * Pass `segmented` for the filled-card treatment. Raised-while-dragging is applied by HrSortableList
 * via SortableJS state classes, so it needs no code here.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering the rows inside HrSortableList's `#item` slot
 * @avoidWhen A row is not reorderable (e.g. a resolved/archived entry) — use HrListItem instead
 * @within hr-sortable-list
 */
defineOptions({ name: 'HrSortableItem' });

defineProps({
  /** Headline text. Falls back to the default slot when null. */
  label: {
    type: String,
    default: null,
  },
  /** Supporting text shown beneath the label. */
  caption: {
    type: String,
    default: null,
  },
  /** Enables tap/click and the interaction state layer — set when the row opens an editor. */
  clickable: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders rows in the M3 "segmented" style — filled surface-container cards. A visual choice;
   * drag behavior is unchanged.
   */
  segmented: {
    type: Boolean,
    default: false,
  },
  /**
   * Removes the segmented card's horizontal inset so it sits flush with its container.
   *
   * `segmented` insets each card by 8px, which is right for a list that owns its surface. It is
   * wrong wherever the list sits inside another layout — a form grid, say — where every neighboring
   * control is flush to the column and the inset reads as misalignment. Has no effect without
   * `segmented`.
   */
  flush: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the row is activated (only meaningful with `clickable`). */
  click: null,
});
</script>

<template>
  <hr-list-item
    v-bind="$props"
    @click="emit('click', $event)"
  >
    <template #leading>
      <hr-drag-handle />
    </template>
    <template
      v-if="$slots.default"
      #default
    >
      <!-- @slot Main content; renders in place of `label` and `caption` when provided. -->
      <slot />
    </template>
    <template
      v-if="$slots.trailing"
      #trailing
    >
      <!-- @slot trailing Trailing content, e.g. an overflow menu or status. -->
      <slot name="trailing" />
    </template>
  </hr-list-item>
</template>
